import { createFileRoute } from '@tanstack/react-router'
import Logo from '@/assets/logo.svg';
import { EmailConfirmationModal } from '@/components/auth/EmailConfirmationModal';
import { ForgotPasswordModal } from '@/components/auth/ForgotPasswordModal';
import { PasswordCriteria } from '@/components/auth/PasswordCriteria';
import LoginHero from '@/components/auth/LoginHero';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTheme } from '@/components/theme-provider';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/useUserStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Github } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';


// Define the form schema with Zod
const loginSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(1, { message: 'Password is required' }),
});

// Define the signup schema with Zod
const signupSchema = z
  .object({
    email: z.string().email({ message: 'Please enter a valid email address' }),
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters' })
      .regex(/[A-Z]/, { message: 'Password must contain an uppercase letter' })
      .regex(/[a-z]/, { message: 'Password must contain a lowercase letter' })
      .regex(/[0-9]/, { message: 'Password must contain a number' })
      .regex(/[^A-Za-z0-9]/, {
        message: 'Password must contain a special character',
      }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

// Define the form types
type LoginFormValues = z.infer<typeof loginSchema>;
type SignupFormValues = z.infer<typeof signupSchema>;

function Login() {
  const { theme } = useTheme();
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const { setSession, setUser, user, initialized } = useUserStore();
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/login' });
  // Include search params in the redirect URL to preserve plan selection
  const from = typeof searchParams.redirect === 'string' ? searchParams.redirect : '/';

  // Initialize react-hook-form with the appropriate schema
  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
  });

  const signupForm = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    mode: 'onChange',
  });

  const latestThemeRef = useRef(theme);

  useEffect(() => {
    latestThemeRef.current = theme;
  }, [theme]);

  useEffect(() => {
    // Force dark theme for the login route without persisting user preference changes
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const hadDarkClass = root.classList.contains('dark');

    root.classList.add('dark');

    return () => {
      if (hadDarkClass) {
        return;
      }

      const latestTheme = latestThemeRef.current;
      const prefersDarkScheme = () => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
          return false;
        }

        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      };
      const shouldKeepDark =
        latestTheme === 'dark' || (latestTheme === 'system' && prefersDarkScheme());

      if (!shouldKeepDark) {
        root.classList.remove('dark');
      }
    };
  }, []);

  // Watch password and confirmPassword for the criteria component
  const watchedPassword = signupForm.watch('password') || '';
  const watchedConfirmPassword = signupForm.watch('confirmPassword') || '';

  // Reset form when switching between login and signup
  useEffect(() => {
    if (isSignUp) {
      loginForm.reset();
    } else {
      signupForm.reset();
    }
  }, [isSignUp, loginForm, signupForm]);

  // If auth is initialized and user exists, redirect immediately
  if (initialized && user) {
    return <Navigate to={from} replace />;
  }

  // If not initialized yet, show nothing to prevent flash
  if (!initialized) {
    return null;
  }

  const handleOAuthLogin = async (provider: 'github' | 'google') => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
      });

      if (error) throw error;
    } catch (error) {
      console.error('OAuth Login Error:', error); // Log the actual error for debugging
      setError('An unexpected error occurred. Please try again.');
    }
  };

  const handleLoginSubmit = async (data: LoginFormValues) => {
    setLoading(true);
    setError('');

    try {
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) throw error;

      if (authData.user && authData.session) {
        setSession(authData.session);
        setUser(authData.user);
        navigate({ to: from });
      }
    } catch (error) {
      console.error('Login Submit Error:', error); // Log the actual error for debugging
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupSubmit = async (data: SignupFormValues) => {
    setLoading(true);
    setError('');

    try {
      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
      });

      if (error) throw error;

      if (authData.user && authData.session) {
        setSession(authData.session);
        setUser(authData.user);
        navigate({ to: from });
      } else {
        // Show email confirmation modal instead of error message
        setConfirmationEmail(data.email);
        setShowEmailConfirmation(true);
      }
    } catch (error) {
      console.error('Signup Submit Error:', error); // Log the actual error for debugging
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark relative min-h-screen overflow-hidden">
      {/* SVG Background from landing page */}
      <div className="absolute inset-0 w-full h-full">
        <svg
          className="absolute inset-0 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1920 1080"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <radialGradient id="bgGradient" cx="50%" cy="50%" r="70%" fx="50%" fy="50%">
              <stop offset="0%" stopColor="#1E1527" />
              <stop offset="70%" stopColor="#0D0910" />
              <stop offset="100%" stopColor="#000000" />
            </radialGradient>
            
            <filter id="purpleGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
            
            <pattern id="dotPattern" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
              <circle cx="15" cy="15" r="0.5" fill="#9B74F2" opacity="0.3" />
            </pattern>
          </defs>
          
          <rect width="100%" height="100%" fill="url(#bgGradient)" />
          <rect width="100%" height="100%" fill="url(#dotPattern)" />
        </svg>
      </div>
      
      <div className="flex flex-col md:flex-row min-h-screen relative z-10">
        {/* Left Column - Login Hero */}
        <div className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-8 lg:p-12 bg-black/30 backdrop-blur-sm">
          <LoginHero />
        </div>

        {/* Right Column - Login Form */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-8 lg:p-12 bg-white/[0.02] backdrop-blur-sm"
        >
          <Card className="w-full max-w-md border-0 shadow-lg">
            <CardContent className="pt-8">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-4 text-center mb-8"
              >
                <img
                  src={Logo}
                  alt="Hooklistener"
                  className="h-8 w-auto mx-auto"
                />
                <div className="space-y-2">
                  <h1 className="text-2xl font-semibold tracking-tight">
                    <span className="text-white">Hook</span>
                    <span className="text-[#B499F2]">listener</span>
                  </h1>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="text-sm text-muted-foreground"
                  >
                    Welcome back
                  </motion.p>
                </div>
              </motion.div>

              {isSignUp ? (
                <form
                  onSubmit={signupForm.handleSubmit(handleSignupSubmit)}
                  className="space-y-4"
                >
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 font-medium flex items-center justify-center gap-2"
                    onClick={() => handleOAuthLogin('github')}
                  >
                    <Github className="w-5 h-5" />
                    Continue with GitHub
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 font-medium flex items-center justify-center gap-2"
                    onClick={() => handleOAuthLogin('google')}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        Or continue with
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="signup-email"
                      className="text-sm font-medium"
                    >
                      Email address
                    </Label>
                    <Input
                      id="signup-email"
                      type="email"
                      className="h-11"
                      placeholder="name@company.com"
                      {...signupForm.register('email')}
                      aria-invalid={
                        signupForm.formState.errors.email ? 'true' : 'false'
                      }
                    />
                    {signupForm.formState.errors.email && (
                      <p className="text-xs text-destructive mt-1">
                        {signupForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="signup-password"
                      className="text-sm font-medium"
                    >
                      Password
                    </Label>
                    <Input
                      id="signup-password"
                      type="password"
                      className="h-11"
                      togglePasswordVisibility={true} // Add toggle prop
                      {...signupForm.register('password')}
                      aria-invalid={
                        signupForm.formState.errors.password ? 'true' : 'false'
                      }
                    />
                    {signupForm.formState.errors.password && (
                      <p className="text-xs text-destructive mt-1">
                        {signupForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="confirmPassword"
                      className="text-sm font-medium"
                    >
                      Confirm Password
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      className="h-11"
                      togglePasswordVisibility={true} // Add toggle prop
                      {...signupForm.register('confirmPassword')}
                      aria-invalid={
                        signupForm.formState.errors.confirmPassword
                          ? 'true'
                          : 'false'
                      }
                    />
                    {signupForm.formState.errors.confirmPassword && (
                      <p className="text-xs text-destructive mt-1">
                        {signupForm.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  <PasswordCriteria
                    password={watchedPassword}
                    confirmPassword={watchedConfirmPassword}
                  />

                  <p className="text-xs text-muted-foreground text-center">
                    By creating an account, you agree to our Terms & Conditions and subscribe to our newsletter.
                  </p>

                  {error && (
                    <p className="text-sm text-destructive text-center">
                      {error}
                    </p>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-11 font-medium"
                    disabled={loading || !signupForm.formState.isValid}
                  >
                    {loading ? 'Please wait...' : 'Create account'}
                  </Button>

                  <p className="text-sm text-muted-foreground text-center">
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setIsSignUp(false)}
                      className="text-primary hover:underline font-medium"
                    >
                      Sign in
                    </button>
                  </p>
                </form>
              ) : (
                <form
                  onSubmit={loginForm.handleSubmit(handleLoginSubmit)}
                  className="space-y-4"
                >
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 font-medium flex items-center justify-center gap-2"
                    onClick={() => handleOAuthLogin('github')}
                  >
                    <Github className="w-5 h-5" />
                    Continue with GitHub
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 font-medium flex items-center justify-center gap-2"
                    onClick={() => handleOAuthLogin('google')}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        Or continue with
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="login-email"
                      className="text-sm font-medium"
                    >
                      Email address
                    </Label>
                    <Input
                      id="login-email"
                      type="email"
                      className="h-11"
                      placeholder="name@company.com"
                      {...loginForm.register('email')}
                      aria-invalid={
                        loginForm.formState.errors.email ? 'true' : 'false'
                      }
                    />
                    {loginForm.formState.errors.email && (
                      <p className="text-xs text-destructive mt-1">
                        {loginForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label
                        htmlFor="login-password"
                        className="text-sm font-medium"
                      >
                        Password
                      </Label>
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-xs text-primary hover:underline font-medium"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input
                      id="login-password"
                      type="password"
                      className="h-11"
                      togglePasswordVisibility={true} // Add toggle prop
                      {...loginForm.register('password')}
                      aria-invalid={
                        loginForm.formState.errors.password ? 'true' : 'false'
                      }
                    />
                    {loginForm.formState.errors.password && (
                      <p className="text-xs text-destructive mt-1">
                        {loginForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  {error && (
                    <p className="text-sm text-destructive text-center">
                      {error}
                    </p>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-11 font-medium"
                    disabled={loading || !loginForm.formState.isValid}
                  >
                    {loading ? 'Please wait...' : 'Sign in'}
                  </Button>

                  <p className="text-sm text-muted-foreground text-center">
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setIsSignUp(true)}
                      className="text-primary hover:underline font-medium"
                    >
                      Create one
                    </button>
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
      {/* Email confirmation modal */}
      <EmailConfirmationModal
        open={showEmailConfirmation}
        onOpenChange={setShowEmailConfirmation}
        email={confirmationEmail}
      />
      {/* Forgot password modal */}
      <ForgotPasswordModal
        open={showForgotPassword}
        onOpenChange={setShowForgotPassword}
      />
    </div>
  );
}

export const Route = createFileRoute('/login')({
  component: Login,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    return {
      redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
    }
  },
})
